import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CalendarDays, ChevronDown, Compass, Gift, Minus, Package, Percent, Plus, ShieldCheck, ShoppingBag, Sun, Truck, Users } from "lucide-react";
import { toast } from "sonner";
import { api, formatPrice, resolveImage } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { recordView } from "@/lib/recentlyViewed";
import ProductCard from "@/components/ProductCard";
import BeeRating from "@/components/BeeRating";
import WaitlistForm from "@/components/WaitlistForm";
import { Seo } from "@/context/SeoContext";
import RichText, { stripRichText } from "@/components/RichText";
import ProductGallery, { productPhotos } from "@/components/ProductGallery";
import ProductReviews, { ProductRatingSummary, useProductReviews } from "@/components/ProductReviews";

export default function ProductPage() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const reviews = useProductReviews(product?.product_id);

  const [notFound, setNotFound] = useState(false);
  const [quantity, setQuantity] = useState(1);
  const [slots, setSlots] = useState(null);
  const [slotId, setSlotId] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [related, setRelated] = useState([]);
  const [waitlistEligible, setWaitlistEligible] = useState(false);
  const [boxTier, setBoxTier] = useState(null);
  const [boxCounts, setBoxCounts] = useState({});
  const [mode, setMode] = useState("once");
  const [subCadence, setSubCadence] = useState(null);
  const [gift, setGift] = useState({ is_gift: false, recipient_name: "", address: "", postal_code: "", city: "", country: "France", message: "", first_delivery_date: "" });
  const { addItem, setIsOpen } = useCart();

  useEffect(() => {
    api.get("/pricing").then(({ data }) => setTiers(data)).catch(() => {});
  }, []);

  useEffect(() => {
    setProduct(null);
    setQuantity(1);
    setSlots(null);
    setSlotId(null);
    setRelated([]);
    api.get(`/products/${productId}`).then(({ data }) => {
      // Ancien QR code / slug : rediriger vers l'URL canonique du produit
      if (data.product_id && data.product_id !== productId) {
        navigate(`/produit/${data.product_id}`, { replace: true });
        return;
      }
      setProduct(data);
      recordView(data);
      api.get(`/products/${data.product_id}/related`).then((r) => setRelated(r.data)).catch(() => setRelated([]));
    }).catch(() => setNotFound(true));
  }, [productId, navigate]);

  useEffect(() => {
    if (product?.bookable) {
      api.get(`/products/${productId}/slots`).then(({ data }) => setSlots(data)).catch(() => setSlots([]));
    }
  }, [product, productId]);

  useEffect(() => {
    setWaitlistEligible(false);
    if (product && !product.bookable) {
      api.get(`/waitlist/status?product_id=${product.product_id}`)
        .then(({ data }) => setWaitlistEligible(!!data.eligible))
        .catch(() => setWaitlistEligible(false));
    }
  }, [product]);

  useEffect(() => {
    if (product?.is_box && product.box_tiers?.length) {
      setBoxTier(product.box_tiers[0]);
      setBoxCounts({});
    } else {
      setBoxTier(null);
      setBoxCounts({});
    }
    const sub = product?.subscription;
    const subOK = product?.is_box && sub?.enabled && ((sub.monthly_price_cents || 0) > 0 || (sub.bimonthly_price_cents || 0) > 0);
    setMode(product?.is_box && !product.box_tiers?.length && subOK ? "sub" : "once");
    setSubCadence(subOK ? ((sub.monthly_price_cents || 0) > 0 ? "monthly" : "bimonthly") : null);
    setGift({ is_gift: false, recipient_name: "", address: "", postal_code: "", city: "", country: "France", message: "", first_delivery_date: "" });
  }, [product]);

  const outOfStock = product ? !product.bookable && (product.stock ?? 1) <= 0 : false;
  const selectedSlot = slots?.find((s) => s.slot_id === slotId) || null;
  const remainingPlaces = selectedSlot ? selectedSlot.capacity - (selectedSlot.booked || 0) : 0;

  // Règle métier : une remise de catégorie masque la remise produit
  const categoryTiers = product ? tiers.filter((t) => t.scope === "category" && t.target_id === product.category) : [];
  const productTiers = product ? tiers.filter((t) => t.scope === "product" && t.target_id === product.product_id) : [];
  const visibleTiers = (categoryTiers.length ? categoryTiers : productTiers).slice().sort((a, b) => a.min_quantity - b.min_quantity);
  const activeTier = [...visibleTiers].reverse().find((t) => quantity >= t.min_quantity) || null;
  const globalT = tiers.filter((t) => t.scope === "global").slice().sort((a, b) => a.min_quantity - b.min_quantity);

  const sub = product?.subscription;
  const subAvailable = Boolean(product?.is_box && sub?.enabled && ((sub.monthly_price_cents || 0) > 0 || (sub.bimonthly_price_cents || 0) > 0));
  const isSub = mode === "sub" && subAvailable;
  const requiredCount = product?.is_box ? (isSub ? (sub?.honeys_per_box || 4) : (boxTier?.count || 0)) : 0;
  const boxTotal = Object.values(boxCounts).reduce((a, b) => a + b, 0);
  const boxComplete = product?.is_box ? requiredCount > 0 && boxTotal === requiredCount : false;
  const subCadencePrice = subCadence === "monthly" ? (sub?.monthly_price_cents || 0) : subCadence === "bimonthly" ? (sub?.bimonthly_price_cents || 0) : 0;
  const boxPriceCents = isSub ? subCadencePrice : (boxTier ? boxTier.price_cents : product?.price_cents);
  const giftForeign = gift.is_gift && gift.country && gift.country.trim().toLowerCase() !== "france";
  const giftIncomplete = gift.is_gift && !(gift.recipient_name.trim() && gift.address.trim() && gift.city.trim());
  const incHoney = (h) => setBoxCounts((c) => (boxTotal >= requiredCount ? c : { ...c, [h]: (c[h] || 0) + 1 }));
  const decHoney = (h) => setBoxCounts((c) => {
    const n = (c[h] || 0) - 1;
    const nc = { ...c };
    if (n <= 0) delete nc[h]; else nc[h] = n;
    return nc;
  });

  const handleAdd = () => {
    if (product.is_box) {
      if (!boxComplete) {
        toast.error(`Choisissez ${requiredCount} miels (${boxTotal}/${requiredCount})`);
        return;
      }
      const selection = Object.entries(boxCounts).flatMap(([h, n]) => Array(n).fill(h));
      const composed = Object.entries(boxCounts).map(([h, n]) => (n > 1 ? `${h} ×${n}` : h)).join(", ");
      if (isSub) {
        if (!subCadence || subCadencePrice <= 0) {
          toast.error("Choisissez une formule d'abonnement");
          return;
        }
        if (giftIncomplete) {
          toast.error("Renseignez le nom et l'adresse du destinataire");
          return;
        }
        const deliveries = subCadence === "monthly" ? 12 : 6;
        addItem(
          { ...product, price_cents: subCadencePrice, box_label: `1ʳᵉ box : ${composed} · puis surprises`, image_url: resolveImage(product.image_url) },
          quantity,
          null,
          selection,
          { subscription: { cadence: subCadence, deliveries }, gift: gift.is_gift ? { ...gift } : null }
        );
        toast.success("Abonnement ajouté au panier");
        setIsOpen(true);
        return;
      }
      addItem({ ...product, price_cents: boxTier.price_cents, box_label: `${boxTier.count} miels : ${composed}`, image_url: resolveImage(product.image_url) }, quantity, null, selection);
      toast.success(`${product.name} composé ajouté au panier`);
      setIsOpen(true);
      return;
    }
    if (product.bookable && !selectedSlot) {
      toast.error("Choisissez d'abord une date de visite");
      return;
    }
    addItem({ ...product, image_url: resolveImage(product.image_url) }, quantity, selectedSlot);
    toast.success(selectedSlot ? `${product.name} — ${selectedSlot.date} ajouté au panier` : `${product.name} ajouté au panier`);
    setIsOpen(true);
  };

  if (notFound) {
    return (
      <div data-testid="product-not-found" className="mx-auto max-w-2xl px-4 py-24 text-center">
        <h1 className="font-serif text-3xl font-semibold text-bark">Cette douceur s'est envolée…</h1>
        <Link to="/boutique" data-testid="back-to-shop" className="mt-6 inline-flex items-center gap-2 text-amber-deep hover:text-bark">
          <ArrowLeft size={16} /> Retour à la boutique
        </Link>
      </div>
    );
  }

  if (!product) {
    return <p data-testid="product-loading" className="py-24 text-center font-serif text-xl text-bark-soft">La ruche s'active…</p>;
  }

  return (
    <div data-testid="product-page" className="mx-auto max-w-7xl px-4 py-12 sm:px-8">
      <Seo
        title={`${product.name} | Pétales de Miel`}
        description={stripRichText(product.description).slice(0, 200)}
        image={product.image_url}
        path={`/produit/${product.product_id}`}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: product.name,
          image: productPhotos(product).map(resolveImage),
          description: stripRichText(product.description),
          brand: { "@type": "Brand", name: "Pétales de Miel" },
          offers: {
            "@type": "Offer",
            price: (product.price_cents / 100).toFixed(2),
            priceCurrency: "EUR",
            availability: outOfStock ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
          },
        }}
      />
      <Link to="/boutique" data-testid="back-to-boutique" className="mb-8 inline-flex items-center gap-2 text-sm font-medium text-bark-soft transition-colors hover:text-amber-deep">
        <ArrowLeft size={16} /> Retour à la boutique
      </Link>

      <div className="grid gap-12 lg:grid-cols-2">
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5 }} className="relative">
          <ProductGallery product={product} mainClass="aspect-square rounded-[2rem] border border-amber-900/10 shadow-xl shadow-amber-900/10" />
          {product.badge && (
            <span data-testid="product-badge" className="absolute left-6 top-6 rounded-full bg-cream/90 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-amber-900/80 backdrop-blur">
              {product.badge}
            </span>
          )}
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.1 }} className="space-y-6">
          <div>
            <p className="text-sm font-medium uppercase tracking-[0.2em] text-stone-mute sm:text-base">{product.weight}</p>
            <h1 data-testid="product-name" className="mt-2 font-serif text-4xl font-semibold leading-tight tracking-tight text-bark">
              {product.name}
            </h1>
            <p data-testid="product-price" className="mt-3 font-serif text-3xl font-semibold text-amber-deep">
              {product.is_box && !isSub && product.box_tiers?.length > 1 && <span className="mr-1 text-lg font-normal text-bark-soft">dès</span>}
              {formatPrice(product.is_box ? boxPriceCents : product.price_cents)}
              {isSub && <span className="ml-1 text-base font-normal text-bark-soft">/an</span>}
            </p>
            <ProductRatingSummary data={reviews} />
          </div>

          <RichText text={product.description} testId="product-description" />

          {product.notes?.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {product.notes.map((note) => (
                <span key={note} data-testid={`tasting-note-${note.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="rounded-full bg-sage-light px-4 py-1.5 text-xs font-medium text-sage">
                  {note}
                </span>
              ))}
            </div>
          )}

          <BeeRating value={product.intensity} />

          <div className="space-y-2 rounded-2xl border border-amber-900/10 bg-white p-6 text-sm">
            <p className="flex items-center gap-2 text-bark-soft">
              <Compass size={16} className="text-honey" />
              <span><strong className="text-bark">Terroir :</strong> {product.origin}</span>
            </p>
            <p className="flex items-center gap-2 text-bark-soft">
              <Sun size={16} className="text-honey" />
              <span><strong className="text-bark">Récolte :</strong> {product.harvest}</span>
            </p>
          </div>

          {product.details?.trim() && (
            <details data-testid="product-details" className="group rounded-2xl border border-amber-900/10 bg-white p-6">
              <summary data-testid="product-details-toggle" className="flex cursor-pointer list-none items-center justify-between gap-3 font-serif text-lg font-semibold text-bark [&::-webkit-details-marker]:hidden">
                Informations complémentaires
                <ChevronDown size={18} className="shrink-0 text-bark-soft transition-transform group-open:rotate-180" />
              </summary>
              <RichText text={product.details} testId="product-details-content" className="mt-4 text-sm" />
            </details>
          )}

          {!product.is_box && visibleTiers.length > 0 && (
            <div data-testid="volume-pricing" className="space-y-3 rounded-2xl border border-honey/40 bg-amber-50/60 p-6">
              <p className="flex items-center gap-2 font-serif text-xl font-semibold text-bark">
                <Percent size={18} className="text-amber-deep" /> Prix dégressifs
              </p>
              <ul className="space-y-1.5 text-sm">
                {visibleTiers.map((t) => (
                  <li
                    key={t.tier_id}
                    data-testid={`volume-tier-${t.tier_id}`}
                    className={`flex items-center justify-between gap-3 rounded-xl px-3 py-2 transition-colors ${
                      activeTier?.tier_id === t.tier_id ? "bg-white font-semibold text-amber-deep shadow-sm" : "text-bark-soft"
                    }`}
                  >
                    <span>Dès {t.min_quantity} {product.bookable ? "participants" : "articles"}</span>
                    <span>−{t.discount_percent} % · {formatPrice(Math.round((product.price_cents * (100 - t.discount_percent)) / 100))} /{product.bookable ? "pers." : "u."}</span>
                  </li>
                ))}
              </ul>
              {activeTier && (
                <p data-testid="volume-tier-active" className="text-xs font-medium text-sage">
                  Remise de {activeTier.discount_percent} % appliquée à votre sélection.
                </p>
              )}
            </div>
          )}

          {!product.is_box && globalT.length > 0 && (
            <div data-testid="global-pricing" className="space-y-2 rounded-2xl border border-sage/40 bg-sage/5 p-5">
              <p className="flex items-center gap-2 font-serif text-lg font-semibold text-bark">
                <Percent size={16} className="text-sage" /> Remise panier
              </p>
              <p className="text-sm text-bark-soft">Cumulez nos différentes douceurs : la remise s'applique sur l'ensemble du panier, quelles que soient les douceurs choisies.</p>
              <ul className="flex flex-wrap gap-2 text-sm">
                {globalT.map((t) => (
                  <li key={t.tier_id} data-testid={`global-tier-${t.tier_id}`} className="rounded-full bg-white px-3 py-1 font-medium text-sage shadow-sm">
                    dès {t.min_quantity} articles : −{t.discount_percent} %
                  </li>
                ))}
              </ul>
            </div>
          )}

          {product.is_box && (boxTier || isSub) && (
            <div data-testid="box-configurator" className="space-y-4 rounded-2xl border border-honey/40 bg-amber-50/60 p-6">
              <p className="flex items-center gap-2 font-serif text-xl font-semibold text-bark">
                <Package size={20} className="text-amber-deep" /> Composez votre coffret
              </p>

              {subAvailable && product.box_tiers.length > 0 && (
                <div className="flex gap-2" data-testid="box-mode-switch">
                  <button
                    type="button"
                    data-testid="box-mode-once"
                    onClick={() => { setMode("once"); setBoxCounts({}); }}
                    className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${mode === "once" ? "bg-amber-deep text-white shadow-sm" : "border border-amber-800/25 bg-white text-bark-soft hover:border-amber-deep"}`}
                  >
                    Achat unique
                  </button>
                  <button
                    type="button"
                    data-testid="box-mode-sub"
                    onClick={() => { setMode("sub"); setBoxCounts({}); }}
                    className={`flex-1 rounded-xl px-4 py-2.5 text-sm font-medium transition-colors ${mode === "sub" ? "bg-amber-deep text-white shadow-sm" : "border border-amber-800/25 bg-white text-bark-soft hover:border-amber-deep"}`}
                  >
                    Abonnement annuel
                  </button>
                </div>
              )}

              {!isSub && product.box_tiers.length > 1 && (
                <div>
                  <p className="mb-2 text-sm font-medium text-bark">Choisissez votre format</p>
                  <div className="flex flex-wrap gap-2" data-testid="box-tier-options">
                    {product.box_tiers.slice().sort((a, b) => a.count - b.count).map((t) => (
                      <button
                        key={t.count}
                        type="button"
                        data-testid={`box-tier-${t.count}`}
                        onClick={() => { setBoxTier(t); setBoxCounts({}); }}
                        className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${boxTier?.count === t.count ? "bg-amber-deep text-white shadow-sm" : "border border-amber-800/25 bg-white text-bark-soft hover:border-amber-deep"}`}
                      >
                        {t.count} miels · {formatPrice(t.price_cents)}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {isSub && (
                <div className="space-y-3">
                  <p className="text-sm font-medium text-bark">Votre formule</p>
                  <div className="grid gap-2 sm:grid-cols-2" data-testid="sub-cadence-options">
                    {(sub.monthly_price_cents || 0) > 0 && (
                      <button
                        type="button"
                        data-testid="sub-cadence-monthly"
                        onClick={() => setSubCadence("monthly")}
                        className={`rounded-xl border px-4 py-3 text-left transition-colors ${subCadence === "monthly" ? "border-amber-deep bg-white" : "border-amber-900/15 bg-white/70 hover:border-amber-deep/50"}`}
                      >
                        <span className="block text-sm font-semibold text-bark">Tous les mois</span>
                        <span className="block text-xs text-stone-mute">12 livraisons · {formatPrice(sub.monthly_price_cents)}/an</span>
                      </button>
                    )}
                    {(sub.bimonthly_price_cents || 0) > 0 && (
                      <button
                        type="button"
                        data-testid="sub-cadence-bimonthly"
                        onClick={() => setSubCadence("bimonthly")}
                        className={`rounded-xl border px-4 py-3 text-left transition-colors ${subCadence === "bimonthly" ? "border-amber-deep bg-white" : "border-amber-900/15 bg-white/70 hover:border-amber-deep/50"}`}
                      >
                        <span className="block text-sm font-semibold text-bark">Tous les 2 mois</span>
                        <span className="block text-xs text-stone-mute">6 livraisons · {formatPrice(sub.bimonthly_price_cents)}/an</span>
                      </button>
                    )}
                  </div>
                  <p className="rounded-xl bg-sage-light px-4 py-2.5 text-xs text-sage">
                    Votre première box suit la composition ci-dessous ; les suivantes sont des surprises de saison choisies par l'apiculteur.
                  </p>
                </div>
              )}

              <div>
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-sm font-medium text-bark">{isSub ? "Votre première box" : "Vos miels"}</p>
                  <span data-testid="box-progress" className={`text-sm font-semibold ${boxComplete ? "text-sage" : "text-amber-deep"}`}>
                    {boxTotal} / {requiredCount}
                  </span>
                </div>
                <div className="space-y-2">
                  {product.box_options.map((o) => {
                    const h = o.name;
                    const n = boxCounts[h] || 0;
                    const atMax = boxTotal >= requiredCount;
                    return (
                      <div key={h} data-testid={`box-option-${h}`} className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 transition-colors ${n > 0 ? "border-amber-deep bg-white" : "border-amber-900/15 bg-white/70"}`}>
                        <div className="flex min-w-0 items-center gap-3">
                          {o.image_url ? (
                            <img src={resolveImage(o.image_url)} alt={h} className="h-11 w-11 shrink-0 rounded-lg border border-amber-900/10 bg-white object-contain" />
                          ) : (
                            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-amber-900/10 bg-cream text-honey">
                              <Package size={16} />
                            </div>
                          )}
                          <span className="truncate text-sm font-medium text-bark">{h}</span>
                        </div>
                        <div className="flex shrink-0 items-center gap-2 rounded-full border border-amber-900/15 bg-white px-2 py-1">
                          <button
                            type="button"
                            data-testid={`box-dec-${h}`}
                            onClick={() => decHoney(h)}
                            disabled={n === 0}
                            className="flex h-7 w-7 items-center justify-center rounded-full text-bark-soft transition-colors hover:text-amber-deep disabled:opacity-30"
                            aria-label={`Retirer un ${h}`}
                          >
                            <Minus size={14} />
                          </button>
                          <span data-testid={`box-count-${h}`} className="w-5 text-center text-sm font-semibold">{n}</span>
                          <button
                            type="button"
                            data-testid={`box-inc-${h}`}
                            onClick={() => incHoney(h)}
                            disabled={atMax}
                            className="flex h-7 w-7 items-center justify-center rounded-full text-bark-soft transition-colors hover:text-amber-deep disabled:opacity-30"
                            aria-label={`Ajouter un ${h}`}
                          >
                            <Plus size={14} />
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <p className="mt-2 text-xs text-stone-mute">
                  {boxComplete ? "Votre coffret est complet ✓" : `Ajoutez encore ${requiredCount - boxTotal} miel(s). Vous pouvez répéter un même miel.`}
                </p>
              </div>

              {isSub && (
                <div className="space-y-3 border-t border-amber-900/10 pt-4">
                  <label data-testid="gift-toggle" className="flex cursor-pointer items-center gap-3 text-sm font-medium text-bark">
                    <input data-testid="gift-checkbox" type="checkbox" checked={gift.is_gift} onChange={(e) => setGift((g) => ({ ...g, is_gift: e.target.checked }))} className="h-4 w-4 accent-amber-deep" />
                    <Gift size={16} className="text-terra" /> C'est un cadeau — livrer chez le destinataire
                  </label>
                  {gift.is_gift && (
                    <div className="space-y-3" data-testid="gift-form">
                      <input data-testid="gift-recipient" value={gift.recipient_name} onChange={(e) => setGift((g) => ({ ...g, recipient_name: e.target.value }))} placeholder="Nom du destinataire *" className="w-full rounded-xl border border-amber-900/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-amber-deep" />
                      <input data-testid="gift-address" value={gift.address} onChange={(e) => setGift((g) => ({ ...g, address: e.target.value }))} placeholder="Adresse (n° et rue) *" className="w-full rounded-xl border border-amber-900/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-amber-deep" />
                      <div className="grid gap-3 sm:grid-cols-3">
                        <input data-testid="gift-postal" value={gift.postal_code} onChange={(e) => setGift((g) => ({ ...g, postal_code: e.target.value }))} placeholder="Code postal" className="rounded-xl border border-amber-900/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-amber-deep" />
                        <input data-testid="gift-city" value={gift.city} onChange={(e) => setGift((g) => ({ ...g, city: e.target.value }))} placeholder="Ville *" className="rounded-xl border border-amber-900/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-amber-deep" />
                        <input data-testid="gift-country" value={gift.country} onChange={(e) => setGift((g) => ({ ...g, country: e.target.value }))} placeholder="Pays" className="rounded-xl border border-amber-900/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-amber-deep" />
                      </div>
                      <div>
                        <label className="mb-1 block text-xs font-medium text-bark">Date de 1ʳᵉ livraison souhaitée</label>
                        <input data-testid="gift-date" type="date" value={gift.first_delivery_date} onChange={(e) => setGift((g) => ({ ...g, first_delivery_date: e.target.value }))} className="w-full rounded-xl border border-amber-900/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-amber-deep" />
                      </div>
                      <textarea data-testid="gift-message" rows={2} value={gift.message} onChange={(e) => setGift((g) => ({ ...g, message: e.target.value }))} placeholder="Votre message cadeau (facultatif)" className="w-full resize-none rounded-xl border border-amber-900/20 bg-white px-4 py-2.5 text-sm outline-none focus:border-amber-deep" />
                      {giftForeign && (
                        <p data-testid="gift-foreign-notice" className="rounded-xl bg-amber-50 px-4 py-2.5 text-xs font-medium text-amber-800">
                          Livraison hors de France : un devis vous sera proposé avant validation pour connaître les frais de port.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {product.bookable && (
            <div data-testid="booking-agenda" className="space-y-3 rounded-2xl border border-amber-900/10 bg-white p-6">
              <p className="flex items-center gap-2 font-serif text-xl font-semibold text-bark">
                <CalendarDays size={20} className="text-amber-deep" /> Choisissez votre créneau
              </p>
              {!slots ? (
                <p className="text-sm text-bark-soft">Chargement de l'agenda…</p>
              ) : slots.length === 0 ? (
                <p data-testid="agenda-empty" className="text-sm text-bark-soft">Aucun créneau disponible pour le moment — revenez bientôt !</p>
              ) : (
                <div className="grid gap-2 sm:grid-cols-2">
                  {slots.map((s) => {
                    const remaining = s.capacity - (s.booked || 0);
                    const full = remaining <= 0;
                    return (
                      <label
                        key={s.slot_id}
                        data-testid={`slot-option-${s.slot_id}`}
                        className={`flex items-center gap-3 rounded-xl border px-4 py-3 transition-colors ${
                          full
                            ? "cursor-not-allowed opacity-50"
                            : slotId === s.slot_id
                              ? "cursor-pointer border-amber-deep bg-amber-50"
                              : "cursor-pointer border-amber-900/15 hover:border-amber-deep/50"
                        }`}
                      >
                        <input
                          type="radio"
                          name="visit-slot"
                          checked={slotId === s.slot_id}
                          disabled={full}
                          onChange={() => {
                            setSlotId(s.slot_id);
                            setQuantity((q) => Math.min(q, remaining));
                          }}
                          className="accent-amber-deep"
                        />
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-medium capitalize text-bark">
                            {new Date(`${s.date}T12:00:00`).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}
                          </span>
                          <span className="block text-xs text-stone-mute">{s.time}</span>
                        </span>
                        <span className={`flex shrink-0 items-center gap-1 text-xs font-medium ${full ? "text-terra" : "text-sage"}`}>
                          <Users size={12} /> {full ? "Complet" : `${remaining} places`}
                        </span>
                      </label>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {outOfStock ? (
            <p data-testid="product-stock-status" className="rounded-xl bg-terra/10 px-4 py-3 text-sm font-medium text-terra">
              Épuisé — ce produit reviendra à la prochaine récolte.
            </p>
          ) : !product.bookable && product.stock <= 5 ? (
            <p data-testid="product-stock-status" className="rounded-xl bg-amber-50 px-4 py-3 text-sm font-medium text-amber-800">
              Plus que {product.stock} en stock — la récolte est limitée.
            </p>
          ) : null}

          {outOfStock && waitlistEligible ? (
            <WaitlistForm product={product} />
          ) : (
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-3 rounded-full border border-amber-900/15 bg-white px-3 py-2">
              <button
                data-testid="quantity-decrease"
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                className="flex h-8 w-8 items-center justify-center rounded-full text-bark-soft hover:text-amber-deep"
                aria-label="Diminuer la quantité"
              >
                <Minus size={15} />
              </button>
              <span data-testid="quantity-value" className="w-6 text-center font-semibold">{quantity}</span>
              <button
                data-testid="quantity-increase"
                onClick={() => setQuantity((q) => Math.min(product.bookable && selectedSlot ? remainingPlaces : 100, q + 1))}
                className="flex h-8 w-8 items-center justify-center rounded-full text-bark-soft hover:text-amber-deep"
                aria-label="Augmenter la quantité"
              >
                <Plus size={15} />
              </button>
            </div>
            {product.bookable && <span className="text-xs text-stone-mute">participant(s)</span>}
            <button
              data-testid="product-add-to-cart"
              onClick={handleAdd}
              disabled={outOfStock || (product.bookable && !selectedSlot) || (product.is_box && !boxComplete) || (isSub && giftIncomplete)}
              className="flex flex-1 items-center justify-center gap-2 rounded-full bg-amber-deep px-7 py-4 font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-95 disabled:cursor-not-allowed disabled:bg-stone-mute/40 disabled:shadow-none sm:flex-none"
            >
              <ShoppingBag size={18} />{" "}
              {outOfStock
                ? "Épuisé"
                : product.is_box
                  ? (!boxComplete
                      ? `Composez vos ${requiredCount} miels`
                      : isSub && giftIncomplete
                        ? "Complétez l'adresse cadeau"
                        : isSub
                          ? "S'abonner"
                          : "Ajouter au panier")
                  : product.bookable
                    ? (selectedSlot ? "Réserver ma visite" : "Choisissez un créneau")
                    : "Ajouter au panier"}
            </button>
          </div>
        )}

          <div className="flex flex-wrap gap-x-6 gap-y-2 border-t border-amber-900/10 pt-5 text-xs text-stone-mute">
            <span className="flex items-center gap-1.5"><Truck size={14} className="text-sage" /> Livraison offerte dès 50 €</span>
            <span className="flex items-center gap-1.5"><ShieldCheck size={14} className="text-sage" /> Paiement sécurisé PayPal</span>
            <span className="flex items-center gap-1.5"><Sun size={14} className="text-sage" /> Récolté à la main</span>
          </div>
        </motion.div>
      </div>

      <ProductReviews data={reviews} />

      {related.length > 0 && (
        <section data-testid="related-products" className="mt-20">
          <h2 className="mb-6 font-serif text-2xl font-semibold text-bark sm:text-3xl">Vous aimerez aussi</h2>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {related.map((p) => (
              <ProductCard key={p.product_id} product={p} />
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
